import { Box, Button, makeStyles, Paper, TextField, Typography } from '@material-ui/core'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import axios from 'axios'
import { saveAs } from 'file-saver'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import ContentWrapper from '../Layout/ContentWrapper'
import { office_location } from '../../modules/shared/enums/office_location'
import ReservationTableLabels from './ReservationTableLabels'

const statuses = {
  pending: 'Në pritje të miratimit',
  approved: 'Miratuar',
  completed: 'Përfunduar',
  cancelled: 'Anuluar',
  rejected: 'Refuzuar'
}
const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    '& .MuiTextField-root': { minWidth: 175 },
    '& .MuiOutlinedInput-notchedOutline': { borderBottom: '1px solid rgba(0,0,0,0.23)' }
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(2),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }
  },
  card: { padding: theme.spacing(2) },
  table: { marginTop: theme.spacing(3) }
}))
const column = (name, label, render) => ({ name, label, options: { sort: true, ...(render ? { customBodyRender: render } : {}) } })

const VehicleReservationReports = () => {
  const classes = useStyles()
  let allowed = false
  try {
    allowed = [1, 5, 6].includes(jwtDecode(localStorage.getItem('token')).user_level)
  } catch {
    allowed = false
  }
  const [filters, setFilters] = useState(() => ({
    from: moment().startOf('month').format('YYYY-MM-DD'),
    to: moment().format('YYYY-MM-DD'),
    vehicleId: '',
    destination: '',
    status: ''
  }))
  const [form, setForm] = useState(() => ({ ...filters, from: moment(filters.from).toDate(), to: moment(filters.to).toDate() }))
  const [reload, setReload] = useState(0)
  const [state, setState] = useState({ loading: true, error: '', data: null })
  const [formError, setFormError] = useState('')
  const [exportError, setExportError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [vehicleChoices, setVehicleChoices] = useState([])
  useEffect(() => {
    if (!allowed) return
    let active = true
    const request = axios.CancelToken.source()
    setState({ loading: true, error: '', data: null })
    setExportError('')
    axios
      .get('vehicle-reservations/reports/utilization', { params: filters, cancelToken: request.token })
      .then(({ data }) => {
        if (
          !data.success ||
          !data.data?.summary ||
          !Array.isArray(data.data.byVehicle) ||
          !Array.isArray(data.data.byDestination) ||
          !Array.isArray(data.data.byPeriod) ||
          !Array.isArray(data.data.vehicleChoices)
        )
          throw new Error('Report unavailable')
        if (active) {
          setVehicleChoices(data.data.vehicleChoices)
          setState({ loading: false, error: '', data: data.data })
        }
      })
      .catch((error) => {
        if (active)
          setState({
            loading: false,
            data: null,
            error:
              Object.values(error.response?.data?.errors || {}).join(' ') ||
              error.response?.data?.message ||
              'Raporti nuk u ngarkua. Provoni përsëri.'
          })
      })
    return () => {
      active = false
      request.cancel('Vehicle report changed or closed')
    }
  }, [allowed, filters, reload])
  const update = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }))
    setFormError('')
  }
  const apply = (event) => {
    event.preventDefault()
    if (
      !form.from ||
      !form.to ||
      !moment(form.from).isValid() ||
      !moment(form.to).isValid() ||
      moment(form.from).isAfter(form.to, 'day') ||
      moment(form.to).diff(moment(form.from), 'days') > 365
    ) {
      setFormError('Zgjidhni një interval të vlefshëm deri në 366 ditë.')
      return
    }
    setFormError('')
    setFilters({ ...form, from: moment(form.from).format('YYYY-MM-DD'), to: moment(form.to).format('YYYY-MM-DD') })
    setReload((value) => value + 1)
  }
  const download = async () => {
    if (exporting || !state.data || state.loading) return
    setExporting(true)
    setExportError('')
    try {
      const response = await axios.get('vehicle-reservations/reports/utilization', {
        params: { ...filters, format: 'csv' },
        responseType: 'blob'
      })
      saveAs(response.data, `vehicle-utilization-${filters.from}-${filters.to}.csv`)
    } catch {
      setExportError('CSV nuk u shkarkua. Provoni përsëri.')
    } finally {
      setExporting(false)
    }
  }
  const options = {
    download: false,
    print: false,
    filter: false,
    search: false,
    viewColumns: false,
    selectableRows: 'none',
    responsive: 'scroll',
    rowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50, 100],
    textLabels: new ReservationTableLabels(
      state.loading ? 'Duke ngarkuar raportin…' : state.error ? 'Raporti nuk është i disponueshëm.' : 'Nuk ka të dhëna për këtë interval.'
    )
  }
  const summary = state.data?.summary
  const cards = [
    ['Kërkesa', 'requests'],
    ['Udhëtime të rezervuara', 'bookedTrips'],
    ['Orë të rezervuara', 'bookedHours'],
    ['Pasagjerë për udhëtim', 'passengers'],
    ['Në pritje', 'pending'],
    ['Anuluar', 'cancelled'],
    ['Refuzuar', 'rejected'],
    ['Raste të jashtëzakonshme', 'exceptionalRequests']
  ]
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ContentWrapper>
        <div className='content-heading'>
          <div>Raportet e automjeteve</div>
        </div>
        <Box mb={2}>
          <Button component={Link} to='/vehicle-reservations'>
            Kthehu te rezervimet
          </Button>
        </Box>
        {!allowed ? (
          <Typography role='alert' color='error'>
            Nuk keni të drejtë të shikoni raportet e automjeteve.
          </Typography>
        ) : (
          <>
            <Paper component='form' onSubmit={apply} noValidate className={classes.filters}>
              {[
                ['from', 'Prej datës'],
                ['to', 'Deri më datën']
              ].map(([field, label]) => (
                <DatePicker
                  key={field}
                  label={label}
                  inputFormat='dd/MM/yyyy'
                  value={form[field]}
                  onChange={(value) => update(field, value)}
                  renderInput={(params) => <TextField {...params} id={`report-${field}`} variant='outlined' size='small' />}
                />
              ))}
              <TextField
                id='report-vehicle'
                label='Automjeti'
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                size='small'
                value={form.vehicleId}
                onChange={(event) => update('vehicleId', event.target.value)}
              >
                <option value=''>Të gjitha</option>
                {vehicleChoices.map((vehicle) => (
                  <option value={vehicle.id} key={vehicle.id}>
                    {[vehicle.vehicle_id, vehicle.license_plate].filter(Boolean).join(' · ')}
                  </option>
                ))}
              </TextField>
              <TextField
                id='report-destination'
                label='Destinacioni'
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                size='small'
                value={form.destination}
                onChange={(event) => update('destination', event.target.value)}
              >
                <option value=''>Të gjitha</option>
                {office_location
                  .filter((location) => !location.value.startsWith('HO-'))
                  .map((location) => (
                    <option value={location.value} key={location.value}>
                      {location.name}
                    </option>
                  ))}
              </TextField>
              <TextField
                id='report-status'
                label='Statusi'
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                variant='outlined'
                size='small'
                value={form.status}
                onChange={(event) => update('status', event.target.value)}
              >
                <option value=''>Të gjitha</option>
                {Object.entries(statuses).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </TextField>
              <Button type='submit' color='primary' variant='contained' disabled={state.loading || exporting}>
                Shfaq raportin
              </Button>
              <Button onClick={download} variant='outlined' disabled={state.loading || !state.data || exporting}>
                {exporting ? 'Duke shkarkuar…' : 'Shkarko CSV'}
              </Button>
            </Paper>
            {(formError || state.error || exportError) && (
              <Box mt={2}>
                <Typography role='alert' color='error'>
                  {formError || state.error || exportError}
                </Typography>
                {state.error && <Button onClick={() => setReload((value) => value + 1)}>Provo përsëri</Button>}
              </Box>
            )}
            <Box mt={2}>
              <Typography variant='body2'>
                Intervali i raportit: {moment(filters.from).format('DD/MM/YYYY')} – {moment(filters.to).format('DD/MM/YYYY')}.
              </Typography>
              <Typography variant='body2' color='textSecondary'>
                Orët përfshijnë vetëm rezervimet e miratuara dhe të përfunduara, nga nisja deri në kthim, pa intervalin 30-minutësh.
                Pasagjerët numërohen për udhëtim; nuk ndahen sipas automjetit.
              </Typography>
            </Box>
            <Box className={classes.cards}>
              {cards.map(([label, field]) => (
                <Paper className={classes.card} key={field}>
                  <Typography variant='body2' color='textSecondary'>
                    {label}
                  </Typography>
                  <Typography variant='h5'>{summary ? summary[field] : '—'}</Typography>
                  {field === 'bookedTrips' && summary && (
                    <Typography variant='caption'>
                      {summary.approved} miratuara · {summary.completed} përfunduara
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
            <Box className={classes.table}>
              <MUIDataTable
                title='Përdorimi sipas automjetit'
                data={state.data?.byVehicle || []}
                options={options}
                columns={[
                  column('vehicle_id', 'Vehicle ID'),
                  column('license_plate', 'Targa'),
                  column('bookedTrips', 'Udhëtime'),
                  column('bookedHours', 'Orë të rezervuara'),
                  column('pending', 'Në pritje'),
                  column('cancelled', 'Anuluar'),
                  column('rejected', 'Refuzuar'),
                  column('destinations', 'Destinacionet', (value) => value.join(', '))
                ]}
              />
            </Box>
            <Box className={classes.table}>
              <MUIDataTable
                title='Përdorimi sipas destinacionit'
                data={state.data?.byDestination || []}
                options={options}
                columns={[
                  column('destination', 'Destinacioni'),
                  column('bookedTrips', 'Udhëtime'),
                  column('bookedHours', 'Orë të rezervuara'),
                  column('passengers', 'Pasagjerë'),
                  column('cancelled', 'Anuluar'),
                  column('exceptionalRequests', 'Raste të jashtëzakonshme')
                ]}
              />
            </Box>
            <Box className={classes.table}>
              <MUIDataTable
                title='Përdorimi sipas ditës'
                data={state.data?.byPeriod || []}
                options={options}
                columns={[
                  column('date', 'Data', (value) => moment(value).format('DD/MM/YYYY')),
                  column('bookedTrips', 'Udhëtime'),
                  column('bookedHours', 'Orë të rezervuara'),
                  column('passengers', 'Pasagjerë'),
                  column('pending', 'Në pritje'),
                  column('cancelled', 'Anuluar')
                ]}
              />
            </Box>
          </>
        )}
      </ContentWrapper>
    </LocalizationProvider>
  )
}

export default VehicleReservationReports
